<template>
  <WithBackdrop
    v-if="computedArticles.length"
    :backdrop="data?.fields?.backdrop"
    :class="[$style.config, $style.wrap]"
    :style="data?.fields?.cssOverride"
    data-dynamic-articles-carousel-v="3.1.0"
  >
    <BaseContainer :max-width="1280">
      <div :class="$style.inner">
        <header :class="$style.header">
          <BaseHeading
            v-if="data?.fields?.header"
            size="h4"
            as="h2"
            :class="$style.heading"
          >
            <span v-html="data?.fields?.header" />
          </BaseHeading>
          <MarkedParser
            v-if="data?.fields?.copy"
            :md="data?.fields?.copy"
            :class="['u-fs-p1', $style.copy]"
          />
        </header>

        <div ref="swiperRef" :class="['swiper', $style.articles]">
          <div class="swiper-wrapper">
            <div
              v-for="article in computedArticles"
              :key="article?.sys?.id"
              :class="[$style.slide, 'swiper-slide']"
            >
              <NuxtLinkLocale
                :to="localePath(`/news/${article?.fields?.path || ''}`)"
                target="_blank"
              >
                <div :class="$style.article">
                  <AspectRatio ratio="545x378">
                    <BasePicture
                      :src="`${article?.fields?.featuredImage?.fields?.file?.url}?fit=thumb&q=90`"
                      alt=""
                    />
                  </AspectRatio>
                  <div :class="$style.articleInfoWrap">
                    <div :class="$style.articleInfo">
                      <div :class="[$style.meta, 'u-type-p3']">
                        <span
                          v-if="
                            !article?.fields?.hidePublishedDateTime &&
                            article?.fields?.publishedDateTime
                          "
                        >
                          {{
                            format(
                              new Date(article.fields.publishedDateTime),
                              'LLL d, yyyy',
                            )
                          }}
                        </span>
                      </div>
                      <BaseHeading as="h3" size="h6" :class="$style.title">
                        {{ article?.fields?.title }}
                      </BaseHeading>
                    </div>
                  </div>
                </div>
              </NuxtLinkLocale>
            </div>
          </div>
          <div :class="$style.buttons">
            <button
              :class="[$style.button, $style.prev]"
              aria-label="previous slide"
              :disabled="isAtBeginning"
              @click="prevSlide"
            >
              <IconCarouselArrowLeft :class="$style.icon" />
            </button>
            <button
              :class="[$style.button, $style.next]"
              aria-label="next slide"
              :disabled="isAtEnd"
              @click="nextSlide"
            >
              <IconCarouselArrowRight :class="$style.icon" />
            </button>
          </div>
        </div>
      </div>
    </BaseContainer>
  </WithBackdrop>
</template>

<script setup lang="ts">
import { format } from 'date-fns'
import type { Swiper } from 'swiper'
import swiper from 'swiper'
import 'swiper/css'

import type {
  TypeArticleSetWithoutUnresolvableLinksResponse,
  TypeArticleSkeleton,
  TypeArticleWithoutUnresolvableLinksResponse,
} from '~/types/contentful'

const props = withDefaults(
  defineProps<{
    data: TypeArticleSetWithoutUnresolvableLinksResponse
    debug?: boolean | string
  }>(),
  {
    debug: false,
  },
)

const { $client, $visibility, $userAccessCheck } = useNuxtApp()
const { locale } = useI18n()
const localePath = useLocalePath()
const { highestUserLevel } = storeToRefs(useUserStore())

const swiperRef = ref<(HTMLDivElement & { swiper: Swiper }) | null>(null)
const swiperInstance = ref<Swiper | null>(null)
const isAtBeginning = ref(true)
const isAtEnd = ref(false)
const isDebug = computed(() => {
  return (
    props.debug === '' ||
    props.debug === true ||
    props.debug === 'true' ||
    props.debug === '1'
  )
})

const computedTags = computed(() => {
  return (
    props.data?.fields?.tags?.filter(instanceOfTag).map((t) => t.sys.id) || []
  )
})

const computedTagMatchMode = computed<'all' | 'any'>(() => {
  const rawMode = String(props.data?.fields?.tagMatchMode || '')
    .trim()
    .toLowerCase()

  return rawMode === 'any' ? 'any' : 'all'
})

const computedBatchSize = computed(() => {
  return props.data?.fields?.batchSize || 3
})

const computedMaxArticles = computed(() => {
  return props.data?.fields?.maxArticles
})

const computedDynamicLimit = computed(() => {
  return computedMaxArticles.value || computedBatchSize.value
})

const asyncKey = computed(
  () =>
    `dynamic-articles-carousel:${props.data?.sys?.id}:${locale.value}:${computedTags.value.join(
      ',',
    )}:${computedTagMatchMode.value}:${computedBatchSize.value}:${computedMaxArticles.value || ''}`,
)

const { data: fetchedArticles, status } = await useAsyncData(
  asyncKey,
  async () => {
    const limit = computedDynamicLimit.value
    const pageSize = computedBatchSize.value
    const maxAttempts = 5

    const allowed: TypeArticleWithoutUnresolvableLinksResponse[] = []
    const seen = new Set<string>()

    let skip = 0
    let attempts = 0
    let hasMore = true

    while (allowed.length < limit && hasMore && attempts < maxAttempts) {
      attempts += 1

      const response =
        await $client.withoutUnresolvableLinks.getEntries<TypeArticleSkeleton>({
          content_type: 'article',
          include: 2,
          locale: locale.value,
          limit: pageSize,
          skip,
          order: ['-fields.publishedDateTime'],
          ...(computedTags.value.length
            ? computedTagMatchMode.value === 'any'
              ? { 'fields.tags.sys.id[in]': computedTags.value.join(',') }
              : { 'fields.tags.sys.id[all]': computedTags.value.join(',') }
            : {}),
        })

      const items = (response?.items ||
        []) as TypeArticleWithoutUnresolvableLinksResponse[]
      let attemptVisible = 0
      let attemptUnknown = 0
      let attemptHasAccess = 0
      let attemptAccepted = 0

      for (const article of items) {
        if (!article?.sys?.id || seen.has(article.sys.id)) continue
        seen.add(article.sys.id)

        const isVisible = $visibility(article?.fields?.visibility)
        const hasAccess = $userAccessCheck(
          article?.fields?.accessLevel,
          highestUserLevel.value,
        )

        if (isVisible === true) attemptVisible += 1
        if (isVisible === null) attemptUnknown += 1
        if (hasAccess) attemptHasAccess += 1

        if (isVisible === true && hasAccess) {
          allowed.push(article)
          attemptAccepted += 1
          if (allowed.length >= limit) break
        }
      }

      if (isDebug.value) {
        console.log('[DynamicArticlesCarousel] fetch attempt', {
          attempt: attempts,
          key: asyncKey.value,
          fetched: items.length,
          accepted: attemptAccepted,
          visibleTrue: attemptVisible,
          visibleUnknown: attemptUnknown,
          hasAccessTrue: attemptHasAccess,
          accumulatedAccepted: allowed.length,
          skip,
          pageSize,
          limit,
        })
      }

      skip += items.length
      hasMore = items.length === pageSize
    }

    if (isDebug.value && allowed.length === 0) {
      console.warn(
        '[DynamicArticlesCarousel] no eligible articles after filtering',
        {
          key: asyncKey.value,
          tags: computedTags.value,
          tagMatchMode: computedTagMatchMode.value,
          locale: locale.value,
          highestUserLevel: highestUserLevel.value,
        },
      )
    }

    return allowed.slice(0, limit)
  },
  {
    watch: [
      () => locale.value,
      () => computedTags.value.join(','),
      () => computedTagMatchMode.value,
      () => computedBatchSize.value,
      () => computedMaxArticles.value,
    ],
    default: () => [],
  },
)

watch(
  () => status.value,
  () => {
    if (!isDebug.value) return
    if (status.value === 'success') {
      console.log('[DynamicArticlesCarousel] fetch complete', {
        key: asyncKey.value,
        length: fetchedArticles.value.length,
      })
    }
  },
  { immediate: true },
)

const computedArticles = computed(() => {
  return fetchedArticles.value || []
})

const prevSlide = () => {
  swiperRef.value?.swiper.slidePrev()
}

const nextSlide = () => {
  swiperRef.value?.swiper.slideNext()
}

const syncNavState = () => {
  const instance = swiperInstance.value
  if (!instance) {
    isAtBeginning.value = true
    isAtEnd.value = false
    return
  }

  isAtBeginning.value = instance.isBeginning
  isAtEnd.value = instance.isEnd
}

const initSwiper = async () => {
  await nextTick()
  if (!swiperRef.value || swiperInstance.value) return

  swiperInstance.value = new swiper(swiperRef.value, {
    slidesPerView: 'auto',
    slidesPerGroup: 1,
    spaceBetween: 20,
  })

  swiperInstance.value.on('slideChange', syncNavState)
  swiperInstance.value.on('update', syncNavState)
  swiperInstance.value.on('resize', syncNavState)
  syncNavState()
}

onMounted(async () => {
  if (computedArticles.value.length > 0) {
    await initSwiper()
  }
})

watch(
  () => computedArticles.value.length,
  async (count) => {
    if (count > 0 && !swiperInstance.value) {
      await initSwiper()
    }
    if (count === 0 && swiperInstance.value) {
      swiperInstance.value.destroy(true, true)
      swiperInstance.value = null
      syncNavState()
    }
  },
  { flush: 'post' },
)

onBeforeUnmount(() => {
  swiperInstance.value?.destroy(true, true)
  swiperInstance.value = null
  syncNavState()
})
</script>

<style lang="scss" module>
.config {
  @include breakpoint-variables(
    '--article-set-wrap-padding',
    (
      base: 45px 0,
      md: 60px 0,
      lg: 90px 0,
    )
  );

  @include breakpoint-variables(
    '--article-set-article-info-padding',
    (
      base: 15px 20px,
      md: 20px 30px,
    )
  );

  @include breakpoint-variables(
    '--article-set-article-slide-width',
    (
      base: 320px,
      xs: 350px,
      sm: 380px,
      md: 420px,
      lg: 500px,
    )
  );
}

.wrap {
  padding: var(--article-set-wrap-padding);
}

.inner {
  display: block;
}

.header {
  text-align: left;
  margin-bottom: 24px;
  max-width: 720px;
}

.heading {
  margin-bottom: 12px;
}

.copy {
  margin-bottom: 0;
}

.articles {
  width: 100%;
  overflow: visible;

  .slide {
    width: var(--article-set-article-slide-width);
  }
}

.buttons {
  display: flex;
  gap: 10px;
  margin-top: 15px;

  @include media('xs') {
    gap: 15px;
    margin-top: 20px;
  }
}

.button {
  z-index: 1;
  background: transparent;
  border: 0;
  padding: 0;
  color: var(--purple);

  display: flex;
  align-items: center;
  justify-content: center;
  width: min-content;
  height: min-content;

  &:hover {
    color: var(--purple-dark);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
}

.icon {
  height: 12px;
  width: 24px;

  @include media('xs') {
    height: 17px;
    width: 34px;
  }
}

.meta {
  text-transform: uppercase;
  margin-bottom: 5px;
}

.title {
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  margin-bottom: 3px;
}

.meta,
.title {
  color: var(--white);
}

.article {
  position: relative;
}

.articleInfoWrap {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;

  display: flex;
  padding: var(--article-set-article-info-padding);
  background: linear-gradient(to top, rgba(0, 0, 0, 1), rgba(0, 0, 0, 0) 60%);
}

.articleInfo {
  margin-top: auto;
}
</style>
